"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { useDebouncedWindowDimensions } from "@shared/hooks/use-window-size";
import { cn } from "@shared/ui/utils";

import TenkiLogo from "~/assets/svg/tenki.svg";
import { env } from "~/env";
import { useOnboarding } from "../providers";
import { Cube } from "./cube";
import { useOnboardingCubes } from "./use-onboarding-cubes";

interface OnboardingFlowContainerProps {
  children: React.ReactNode;
  className?: string;
  overflowChild?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  showAnimatedCubes?: boolean;
}

const ENABLE_AUTO_ANIMATION = ["/login", "/recovery"];

const OnboardingFlowContainer = React.forwardRef<HTMLDivElement, OnboardingFlowContainerProps>(
  ({ children, className, overflowChild, showHeader = true, showFooter = true, showAnimatedCubes = false }, ref) => {
    const pathname = usePathname();
    const { width: viewportWidth, height: viewportHeight } = useDebouncedWindowDimensions();
    const containerWidth = viewportWidth / 2;
    const containerHeight = viewportHeight;
    const enableAutoAnimation = showAnimatedCubes
      ? true
      : ENABLE_AUTO_ANIMATION.some((path) => pathname?.includes(path));

    const { progress } = useOnboarding();

    const containerRef = React.useRef<HTMLDivElement>(null);

    const CUBE_WIDTH = 71;
    const CUBE_HEIGHT = 85;

    const gridDimensions = React.useMemo(() => {
      const rowAddition = Math.floor(containerHeight / 200);
      const columnsPerRow = Math.ceil(containerWidth / CUBE_WIDTH);
      const numberOfRows = Math.ceil(containerHeight / CUBE_HEIGHT) + rowAddition;
      return {
        columnsPerRow,
        numberOfRows,
        totalCubes: columnsPerRow * numberOfRows,
      };
    }, [containerWidth, containerHeight]);
    const { columnsPerRow, numberOfRows } = gridDimensions;

    const cubes = useOnboardingCubes({
      progress: progress.progress,
      gridDimensions,
      enableAutoAnimation,
    });

    const gridStyle = {
      "--grid-columns": columnsPerRow,
      "--grid-rows": numberOfRows,
    } as React.CSSProperties;

    return (
      <div className="flex h-screen w-full items-start" ref={ref}>
        <div
          className={cn(
            "bg-foundation relative z-100 flex h-screen w-[50%] flex-1 flex-col px-12",
            overflowChild && "overflow-y-auto",
          )}
        >
          {showHeader && (
            <section>
              <div className="z-10 flex items-center justify-center py-6 pt-10">
                <TenkiLogo />
              </div>
            </section>
          )}

          <section className={cn("align-center mx-auto flex w-[400px] flex-1 flex-col justify-center", className)}>
            <div>{children}</div>
          </section>

          {showFooter && (
            <div>
              {(pathname.includes("/auth/registration") || pathname.includes("/auth/verification")) && (
                <p className="text-static-secondary mb-3 text-center text-xs">
                  By signing up, you accept our{" "}
                  <Link
                    href={`${env.NEXT_PUBLIC_DOCS_UI}/terms-of-service`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-solid underline-offset-[2.5px]"
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link
                    href={`${env.NEXT_PUBLIC_DOCS_UI}/privacy-policy`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-solid underline-offset-[2.5px]"
                  >
                    Privacy Policy
                  </Link>
                </p>
              )}
              <p className="text-static-secondary mb-8 text-center text-sm">
                © Tenki <span>{new Date().getFullYear()}</span> Version 1.0
              </p>
            </div>
          )}
        </div>
        <div className="relative h-screen w-[50%] flex-1 overflow-hidden">
          <div className="bg-onboarding-overlay absolute inset-0 z-60" />
          {
            <div className="absolute top-0 z-50 h-full w-full overflow-hidden" ref={containerRef}>
              <div
                className="absolute -top-8 right-[-2rem] grid h-[108%] w-[110%] grid-cols-[repeat(var(--grid-columns),minmax(0,1fr))] grid-rows-[repeat(var(--grid-rows),minmax(0,1fr))]"
                style={{
                  ...gridStyle,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {Array.from({ length: numberOfRows }).map((_, rowIndex) => (
                  <div
                    key={rowIndex}
                    style={{
                      display: "flex",
                      marginTop: rowIndex > 0 ? "-22px" : "0",
                      marginLeft: rowIndex % 2 === 1 ? `${(CUBE_WIDTH + 2) / 2}px` : "0",
                    }}
                    className="relative z-30"
                  >
                    {cubes.slice(rowIndex * columnsPerRow, (rowIndex + 1) * columnsPerRow).map((cube) => (
                      <Cube key={cube.id} {...cube} />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          }
        </div>
      </div>
    );
  },
);

OnboardingFlowContainer.displayName = "OnboardingFlowContainer";

export { OnboardingFlowContainer };
